import { LuvImage, Modal } from '@/components/ui';
import React, { Dispatch, FC, SetStateAction } from 'react';
import Image from 'next/image';
import {
  ShopifyProduct,
  ShopifyVariant,
  getProductInventoryStatus,
} from '@/lib/shopify';
import {
  TypeAsset,
  TypeProduct as InternalProduct,
} from '@/lib/api/productCatalog/types';
import VariantSelect from './VariantSelect';
import useProductContext from '@/context/ProductContext';
import { InventoryStatus, AddButton } from '../InventoryStatus';
import { TypeImage, TypeVideo } from '@/core/generated-types';
import { CarouselProducts } from '../Carousel';
import { TypeSubscriberShopPageTranslations } from '@/utils/translations';
import { RichText } from '../RichText';
import { Document } from '@contentful/rich-text-types';

interface VariantModalProps {
  isOpen: boolean;
  handleClose: () => void;
  addToCart: () => void;
  asset?: TypeAsset | undefined;
  selectedVariant: ShopifyVariant;
  setSelectedVariant: Dispatch<SetStateAction<ShopifyVariant>>;
  translations: TypeSubscriberShopPageTranslations;
  isProductAddedToCart?: boolean;
}

const VariantModal: FC<VariantModalProps> = ({
  isOpen,
  handleClose,
  addToCart,
  asset,
  selectedVariant,
  setSelectedVariant,
  translations,
  isProductAddedToCart,
}) => {
  const { internalProduct, shopifyProduct, product } = useProductContext();
  const { name, description } = internalProduct as InternalProduct;
  const { productDetails, longDescription } = product?.fields || {};
  const { formattedPrice } = selectedVariant;
  const inventoryStatus = getProductInventoryStatus(
    shopifyProduct as ShopifyProduct,
    selectedVariant,
  );
  const { selectLabel } = translations;
  const descriptionContent = longDescription || description;

  const renderProductImage = () => {
    if (product) {
      const {
        fields: { featuredImage, productImages },
      } = product;
      return productImages ? (
        <CarouselProducts
          components={productImages as (TypeVideo | TypeImage)[]}
        />
      ) : (
        <LuvImage
          imageData={featuredImage}
          alt={featuredImage.fields.altText}
        />
      );
    }

    if (asset) {
      return (
        <Image
          src={asset.uri}
          alt={asset.alt_text || `${name} image`}
          height={240}
          width={320}
          className="mx-auto mb-2 h-[240px] object-contain"
        />
      );
    }

    return null;
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <div className="container max-w-sm lg:grid lg:max-w-[936px] lg:grid-cols-2 lg:pt-6">
        {renderProductImage()}
        <div className="grid-item">
          <div className="mb-2 flex justify-between text-lg font-bold">
            <p>{name}</p>
            <p>{formattedPrice}</p>
          </div>
          {productDetails ? (
            <RichText content={productDetails as Document} />
          ) : (
            descriptionContent && (
              <p
                className="mb-2"
                dangerouslySetInnerHTML={{ __html: descriptionContent }}
              />
            )
          )}
          <VariantSelect
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            selectLabel={selectLabel}
          />
          <div className="mt-4 border-t border-grey-midtone pt-4">
            {inventoryStatus && (
              <div className="mb-4">
                <InventoryStatus
                  inventoryStatus={inventoryStatus}
                  product={product}
                />
              </div>
            )}
            <AddButton
              inventoryStatus={inventoryStatus}
              onClick={() => {
                addToCart();
                handleClose();
              }}
              isSelected={isProductAddedToCart}
              product={product}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default VariantModal;
